import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { contactsApiDatasource } from './contacts-api.datasource';

/** Mesmo intervalo do filtro de e-mail da página de Usuários. */
const DEBOUNCE_MS = 400;

/**
 * O backend exige `filter` com 2+ caracteres (`SearchContactsQueryDto`); aqui é
 * a primeira linha de defesa, para não disparar request que já sairia 400.
 */
const MIN_LENGTH = 2;

/**
 * Autocomplete de contato com duas travas contra request em excesso: debounce no
 * texto e piso de tamanho. O termo debounced entra na `queryKey`, então digitar
 * e apagar de volta para um valor já buscado sai do cache.
 */
export function useContactSearch(term: string) {
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(
      () => setDebouncedTerm(term.trim()),
      DEBOUNCE_MS,
    );
    return () => clearTimeout(timer.current);
  }, [term]);

  const enabled = debouncedTerm.length >= MIN_LENGTH;

  const query = useQuery({
    queryKey: ['identity', 'contacts', debouncedTerm],
    queryFn: () => contactsApiDatasource.searchContacts(debouncedTerm),
    enabled,
    staleTime: 1000 * 60,
  });

  return {
    options: query.data ?? [],
    isLoading: enabled && query.isFetching,
    /** Digitou pouco para buscar — a UI mostra a dica em vez de "sem resultado". */
    isTermTooShort: term.trim().length > 0 && term.trim().length < MIN_LENGTH,
    /** `true` só depois de uma busca que voltou vazia. */
    isEmpty: enabled && query.isSuccess && (query.data?.length ?? 0) === 0,
  };
}
